import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { UzButton, UzInput } from '@utazzo/ui';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import React, { useMemo } from 'react';

import {
  createFooterLink,
  createFooterSection,
  flatToFooterSections,
  footerSectionsToFlat,
  type FooterSectionGroup,
} from './footer-menu.utils';
import { isNavHrefLocked, navHrefLockHint, type FlatDraftItem } from './navigation-tree.utils';

interface Props {
  items: FlatDraftItem[];
  canWrite: boolean;
  onChange: (items: FlatDraftItem[]) => void;
}

const SortableSection = ({
  group,
  canWrite,
  onChangeGroup,
  onRemoveSection,
}: {
  group: FooterSectionGroup;
  canWrite: boolean;
  onChangeGroup: (next: FooterSectionGroup) => void;
  onRemoveSection: () => void;
}): React.JSX.Element => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: group.section.id,
    disabled: !canWrite,
  });

  const patchSection = (label: string): void => {
    onChangeGroup({ ...group, section: { ...group.section, label } });
  };

  const patchItem = (id: string, patch: Partial<FlatDraftItem>): void => {
    onChangeGroup({
      ...group,
      items: group.items.map((i) => (i.id === id ? { ...i, ...patch } : i)),
    });
  };

  const removeItem = (id: string): void => {
    onChangeGroup({ ...group, items: group.items.filter((i) => i.id !== id) });
  };

  const addItem = (): void => {
    onChangeGroup({ ...group, items: [...group.items, createFooterLink()] });
  };

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`rounded-lg border border-(--border) bg-(--bg) ${
        isDragging ? 'z-10 opacity-70 shadow-lg' : ''
      }`}
    >
      <div className="flex items-center gap-2 border-b border-(--border) bg-(--z-50) px-3 py-2">
        <button
          type="button"
          className="cursor-grab text-(--fg-faint) hover:text-(--fg) active:cursor-grabbing disabled:opacity-30"
          disabled={!canWrite}
          aria-label="Drag section"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <span className="shrink-0 rounded bg-(--primary)/10 px-2 py-0.5 text-[10px] font-bold tracking-wide text-(--primary) uppercase">
          Section
        </span>
        <UzInput
          value={group.section.label}
          disabled={!canWrite}
          onChange={(e) => patchSection(e.target.value)}
          placeholder="e.g. About Utazzo"
          className="h-8 flex-1 font-semibold uppercase"
        />
        {canWrite && (
          <button
            type="button"
            onClick={onRemoveSection}
            className="text-(--fg-faint) hover:text-(--ds-error)"
            aria-label="Remove section"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="space-y-2 p-3">
        <p className="text-[11px] text-(--fg-faint)">
          Links in this section appear below the yellow heading on the website.
        </p>
        {group.items.map((item) => (
          <FooterLinkRow
            key={item.id}
            item={item}
            canWrite={canWrite}
            onChange={(patch) => patchItem(item.id, patch)}
            onRemove={() => removeItem(item.id)}
          />
        ))}
        {canWrite && (
          <UzButton type="button" size="sm" variant="outline" onClick={addItem}>
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Add item
          </UzButton>
        )}
      </div>
    </div>
  );
};

const FooterLinkRow = ({
  item,
  canWrite,
  onChange,
  onRemove,
}: {
  item: FlatDraftItem;
  canWrite: boolean;
  onChange: (patch: Partial<FlatDraftItem>) => void;
  onRemove: () => void;
}): React.JSX.Element => {
  const hrefLocked = isNavHrefLocked(item);
  const lockHint = navHrefLockHint[item.itemType];

  return (
    <div className="flex flex-wrap items-start gap-2 rounded-md border border-(--border) bg-(--z-50) p-2">
      <UzInput
        value={item.label}
        disabled={!canWrite}
        onChange={(e) => onChange({ label: e.target.value })}
        placeholder="Link label"
        className="h-8 min-w-[140px] flex-1"
      />
      <UzInput
        value={item.href ?? ''}
        disabled={!canWrite || hrefLocked}
        readOnly={hrefLocked}
        onChange={(e) => onChange({ href: e.target.value || null })}
        placeholder="/path or https://…"
        className="h-8 min-w-[180px] flex-1 font-mono text-xs"
      />
      {hrefLocked && lockHint && <p className="w-full text-[10px] text-(--fg-faint)">{lockHint}</p>}
      {canWrite && (
        <button
          type="button"
          onClick={onRemove}
          className="text-(--fg-faint) hover:text-(--ds-error)"
          aria-label="Remove link"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

/** Footer editor: sections (yellow headings) with nested link items. */
export const FooterMenuStructurePanel = ({
  items,
  canWrite,
  onChange,
}: Props): React.JSX.Element => {
  const deleted = useMemo(() => items.filter((i) => i.isDeleted), [items]);
  const groups = useMemo(() => flatToFooterSections(items), [items]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const applyGroups = (next: FooterSectionGroup[]): void => {
    onChange(footerSectionsToFlat(next, deleted));
  };

  const addSection = (): void => {
    applyGroups([...groups, { section: createFooterSection(), items: [] }]);
  };

  const onDragEndSections = (e: DragEndEvent): void => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = groups.findIndex((g) => g.section.id === active.id);
    const to = groups.findIndex((g) => g.section.id === over.id);
    if (from < 0 || to < 0) return;
    applyGroups(arrayMove(groups, from, to));
  };

  const removeSection = (sectionId: string): void => {
    const removed = groups.find((g) => g.section.id === sectionId);
    if (!removed) return;
    const removedIds = new Set([removed.section.id, ...removed.items.map((i) => i.id)]);
    onChange(items.map((i) => (removedIds.has(i.id) ? { ...i, isDeleted: true } : i)));
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-(--fg)">Footer sections</h2>
          <p className="mt-1 text-xs text-(--fg-faint)">
            Each section is a yellow heading on the site. Add links inside each section - they
            appear in a dot-separated row below the heading.
          </p>
        </div>
        {canWrite && (
          <UzButton type="button" size="sm" onClick={addSection}>
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Add section
          </UzButton>
        )}
      </div>

      {groups.length === 0 ? (
        <p className="rounded border border-dashed border-(--border-strong) py-12 text-center text-sm text-(--fg-faint)">
          Add a section (e.g. &quot;About Utazzo&quot;, &quot;International Trips&quot;), then add
          links inside it.
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEndSections}
        >
          <SortableContext
            items={groups.map((g) => g.section.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-col gap-3">
              {groups.map((group) => (
                <SortableSection
                  key={group.section.id}
                  group={group}
                  canWrite={canWrite}
                  onChangeGroup={(next) =>
                    applyGroups(groups.map((g) => (g.section.id === next.section.id ? next : g)))
                  }
                  onRemoveSection={() => removeSection(group.section.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};
